/**
 * Server signing key management and verification.
 *
 * The server signs attestation payloads with ECDSA P-256.
 * This module fetches the server's public key from the API and verifies signatures.
 */
import canonicalize from 'canonicalize';

/** Cached server public keys by key ID (populated at runtime from /api/server-key) */
const cachedKeys: Record<string, JsonWebKey> = {};

/**
 * Fetch the server's current public key from the API.
 * Caches the result for the session.
 */
export async function fetchServerPublicKey(): Promise<{
  keyId: string;
  publicKey: JsonWebKey;
} | null> {
  try {
    const resp = await fetch("/api/server-key");
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.keyId && data.publicKey) {
      cachedKeys[data.keyId] = data.publicKey;
      return { keyId: data.keyId, publicKey: data.publicKey };
    }
    return null;
  } catch (e) {
    console.error("Failed to fetch server public key:", e);
    return null;
  }
}

/**
 * Get the server's public key for a given key ID.
 * Checks local cache first, then fetches from the API, R2, or GitHub Pages raw fallback.
 */
export async function getServerPublicKey(
  keyId: string,
): Promise<JsonWebKey | null> {
  // 1. Check local cache
  if (cachedKeys[keyId]) return cachedKeys[keyId];

  // 2. Try API (supports ?keyId= for historical lookup)
  try {
    const resp = await fetch(`/api/server-key?keyId=${encodeURIComponent(keyId)}`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.publicKey) {
        cachedKeys[keyId] = data.publicKey;
        return data.publicKey;
      }
    }
  } catch (e) {
    console.warn("API key lookup failed, trying fallback channels:", e);
  }

  // 3. Fallback: R2 CDN (direct)
  // ⚠️ PLACEHOLDER: Replace with actual R2 public URL after enabling Public Access in Cloudflare Settings
  const R2_BASE = "https://keys.signed.plus";
  try {
    const r2Resp = await fetch(`${R2_BASE}/keys/${keyId}.json`);
    if (r2Resp.ok) {
      const data = await r2Resp.json();
      if (data.publicKey) {
        cachedKeys[keyId] = data.publicKey;
        return data.publicKey;
      }
    }
  } catch { /* R2 unavailable */ }

  // 4. Fallback: GitHub raw CDN
  const GH_BASE = "https://raw.githubusercontent.com/andersdn/signedplus-public-keys/main";
  try {
    const ghResp = await fetch(`${GH_BASE}/keys/${keyId}.json`);
    if (ghResp.ok) {
      const data = await ghResp.json();
      if (data.publicKey) {
        cachedKeys[keyId] = data.publicKey;
        return data.publicKey;
      }
    }
  } catch { /* GitHub unavailable */ }

  // 5. Legacy fallback: try fetching current key (might match "alpha-1")
  const fetched = await fetchServerPublicKey();
  if (fetched && fetched.keyId === keyId) {
    return fetched.publicKey;
  }

  // Key ID not found
  return cachedKeys[keyId] || null;
}

/**
 * Deterministically serialize a payload for signing/verification using RFC 8785 (JCS).
 * Ensures cross-platform consistency of JSON bytes.
 */
export function canonicalizePayload(payload: Record<string, any>): string {
  const serialized = canonicalize(payload);
  return serialized || "{}";
}

/**
 * Verify a server ECDSA signature against a payload.
 *
 * @param payload - The attestation payload (without the `signature` field)
 * @param signatureBase64 - The base64-encoded ECDSA signature
 * @param serverKeyId - The key ID to look up the public key
 * @returns true if valid, false if invalid or key not found
 */
export async function verifyServerSignature(
  payload: Record<string, any>,
  signatureBase64: string,
  serverKeyId: string,
): Promise<boolean> {
  const publicKeyJwk = await getServerPublicKey(serverKeyId);
  if (!publicKeyJwk) {
    console.warn(`Unknown server key ID: ${serverKeyId}`);
    return false;
  }

  try {
    const message = canonicalizePayload(payload);

    const key = await crypto.subtle.importKey(
      "jwk",
      { ...publicKeyJwk, key_ops: ["verify"] },
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );

    const sigBytes = Uint8Array.from(atob(signatureBase64), (c) =>
      c.charCodeAt(0),
    );

    return await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      sigBytes,
      new TextEncoder().encode(message),
    );
  } catch (e) {
    console.error("Server signature verification error:", e);
    return false;
  }
}
